/* eslint-disable no-unused-vars */
/**
 * Shared terrain calculation functions used by terrain layers and workers
 * Available for both React components (fallback) and Web Workers
 */

/**
 * Decode DEM from RGB elevation data (Terrarium format)
 * Standard Web Mercator DEM encoding: elevation = (R * 256 + G + B / 256) - 32768
 *
 * Reference: Observable "Slope and Aspect in Leaflet"
 * Uses Terrarium RGB format (newer standard) instead of Mapbox RGB format.
 * Both encode elevation in RGB channels but with slightly different scaling.
 */
export function decodeDEM (data) {
    const dem = new Float32Array(data.length / 4);
    for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        dem[i / 4] = (r * 256 + g + b / 256) - 32768;
    }
    return dem;
}

/**
 * Check if elevation is valid (above sea level)
 * Filters out water and invalid pixels
 */
export function isValidElevation (elevation) {
    return elevation > 0;
}

/**
 * Calculate Sobel gradient at a pixel location
 * Returns {gx, gy} gradient components
 * Matches slopeWorker implementation to avoid edge artifacts
 *
 * Reference: Observable "Slope and Aspect in Leaflet"
 * Uses Sobel operator instead of distance-based gradient for several advantages:
 * - Works consistently across all zoom levels
 * - Uses local 3×3 neighborhood (robust to noise)
 * - Faster computation than distance-based methods
 * - Requires zoom-adaptive normalization to handle "fractal ruler" problem
 *
 * Sobel kernel: gradients in X and Y directions
 * gx/gy are divided by 8 (kernel normalization)
 */
export function calculateSobel (dem, width, height, x, y) {
    // Handle edges by clamping coordinates to valid range
    const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
    const getHeight = (px, py) => {
        const cx = clamp(px, 0, width - 1);
        const cy = clamp(py, 0, height - 1);
        return dem[cy * width + cx];
    };
    // Sobel X gradient
    const gx = (
        -1 * getHeight(x - 1, y - 1) +
        1 * getHeight(x + 1, y - 1) +
        -2 * getHeight(x - 1, y) +
        2 * getHeight(x + 1, y) +
        -1 * getHeight(x - 1, y + 1) +
        1 * getHeight(x + 1, y + 1)
    ) / 8;
    // Sobel Y gradient
    const gy = (
        -1 * getHeight(x - 1, y - 1) +
        1 * getHeight(x - 1, y + 1) +
        -2 * getHeight(x, y - 1) +
        2 * getHeight(x, y + 1) +
        -1 * getHeight(x + 1, y - 1) +
        1 * getHeight(x + 1, y + 1)
    ) / 8;
    return { gx, gy };
}

/**
 * Apply Gaussian blur to DEM data to reduce noise at high zoom levels
 * Uses 5×5 Gaussian kernel with sigma=1.0
 * Returns new Float32Array with blurred data
 *
 * Reference: Observable article identifies "binary" visualization artifacts at zoom > 15
 * due to small elevation differences between neighboring pixels (~2.4m pixel size).
 * Gaussian blur smooths these artifacts while preserving larger topographic features.
 *
 * Applied only at zoom > 15 to maintain detail at lower zoom levels.
 */
export function gaussianBlur (dem, width, height) {
    const blurred = new Float32Array(dem.length);
    const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
    // 5x5 Gaussian kernel with sigma=1.0
    const kernel = [
        1, 4, 7, 4, 1,
        4, 16, 26, 16, 4,
        7, 26, 41, 26, 7,
        4, 16, 26, 16, 4,
        1, 4, 7, 4, 1,
    ];
    const kernelSum = 273; // sum of kernel values

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            let sum = 0;
            let idx = 0;
            // Apply 5x5 kernel centered at (x, y)
            for (let ky = -2; ky <= 2; ky++) {
                for (let kx = -2; kx <= 2; kx++) {
                    const cx = clamp(x + kx, 0, width - 1);
                    const cy = clamp(y + ky, 0, height - 1);
                    sum += dem[cy * width + cx] * kernel[idx];
                    idx++;
                }
            }
            blurred[y * width + x] = sum / kernelSum;
        }
    }
    return blurred;
}

/**
 * Calculate pixel size in meters based on zoom level
 * Uses Earth's circumference and Web Mercator projection
 */
export function calculatePixelMeters (z, tileSize = 512) {
    const earthCircumference = 40075000; // meters
    const tilesAtZoom = Math.pow(2, z);
    const tileMeters = earthCircumference / tilesAtZoom;
    return tileMeters / tileSize;
}

/**
 * Calculate slope in degrees from Sobel gradient
 * Accounts for geographic pixel scaling based on zoom level
 * Note: gradients are already divided by 8 in calculateSobel, use as-is
 */
export function calculateSlope (gx, gy, pixelMeters) {
    const rise = Math.sqrt(gx * gx + gy * gy);
    const run = 2 * pixelMeters;
    const slope = Math.atan(rise / run) * (180 / Math.PI);
    return Math.min(slope, 90);
}

/**
 * Normalize slope based on zoom level to reduce artifacts
 * At low zoom levels (7-10): slope values are exaggerated
 * At high zoom levels (15+): slope values may be too subtle
 * This function applies zoom-adaptive normalization
 *
 * Reference: Observable article highlights the "fractional ruler problem":
 * "Topographical measurements are fractal in nature... measurements depend on
 * the size of your ruler. If it's too big, we may miss mountains. If it's too
 * small, we may be measuring the slope of a snail's shell."
 *
 * At low zoom, neighboring pixels are far apart (10-20km at zoom 4):
 * - Gradient magnitude becomes artificially large
 * - Slopes appear too steep
 * - Multiplying by 0.6-0.8 corrects this exaggeration
 *
 * At high zoom (15+), blur is applied separately to handle noise artifacts.
 *
 * Formula: zoomFactor = 0.6 + (zoom - 7) / 8 * 0.4
 * - Zoom 7:  factor = 0.6  (reduce slope by 40%)
 * - Zoom 15: factor = 1.0  (no reduction, blur already applied)
 */
export function normalizeSlope (slope, zoom) {
    // Zoom factor: adjust slope range based on zoom level
    // At zoom 7: multiply by 0.6 (reduce exaggeration)
    // At zoom 15+: multiply by 1.0 (no change, artifacts already filtered)
    const minZoom = 7;
    const maxZoom = 15;
    const clampedZoom = Math.max(minZoom, Math.min(zoom, maxZoom));
    const zoomFactor = 0.6 + (clampedZoom - minZoom) / (maxZoom - minZoom) * 0.4;
    return slope * zoomFactor;
}

/**
 * Calculate aspect (direction) in degrees from Sobel gradient components
 * Input gradients should be raw Sobel output (divided by 8 from calculateSobel)
 * Returns angle 0-360 where:
 *   0° = North
 *   90° = East
 *   180° = South
 *   270° = West
 * This is the shared calculation used by both aspectWorker and compositeWorker
 */
export function calculateAspect (gx, gy) {
    // Note: gx and gy from calculateSobel are already divided by 8
    // The calculation is the same as in aspectWorker:
    // aspect = atan2(-gy, -gx) + 90 (converts from math coords to geographic)
    let aspect = Math.atan2(-gy, -gx) * (180 / Math.PI);
    aspect = aspect + 90;
    if (aspect < 0) {
        aspect += 360;
    }
    if (aspect >= 360) {
        aspect -= 360;
    }
    return aspect;
}
